import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';

export type PagedFeedMode = 'manual' | 'infinite' | 'viewport';

@Component({
  selector: 'lib-paged-feed',
  standalone: true,
  imports: [NgTemplateOutlet],
  template: `
    <section class="paged-feed">
      @if (loading && items.length === 0) {
        <div class="paged-feed__state">
          {{ loadingText }}
        </div>
      } @else if (!!errorText && items.length === 0) {
        <div class="paged-feed__state paged-feed__state--error">
          <div>{{ errorText }}</div>
          <button
            type="button"
            class="paged-feed__action"
            (click)="retry.emit()"
          >
            {{ retryText }}
          </button>
        </div>
      } @else if (!loading && !errorText && items.length === 0) {
        <div class="paged-feed__state">
          {{ emptyText }}
        </div>
      } @else {
        @if (collectionTemplate) {
          <ng-container
            [ngTemplateOutlet]="collectionTemplate"
            [ngTemplateOutletContext]="{ $implicit: items, items: items }"
          />
        } @else {
          @if (itemTemplate) {
            <div class="paged-feed__items" [class]="itemsClass">
              @for (item of items; track trackItem($index, item); let index = $index) {
                <ng-container
                  [ngTemplateOutlet]="itemTemplate"
                  [ngTemplateOutletContext]="{ $implicit: item, index: index }"
                />
              }
            </div>
          }
        }

        @if (loadingMore && mode !== 'manual') {
          <div class="paged-feed__append-state">
            {{ loadingMoreText }}
          </div>
        }

        @if (hasMore && mode === 'manual') {
          <div class="paged-feed__append-state">
            <button
              type="button"
              class="paged-feed__action paged-feed__action--primary"
              [disabled]="loadingMore"
              (click)="loadMore.emit()"
            >
              {{ loadingMore ? loadingMoreText : loadMoreText }}
            </button>
          </div>
        }

        @if (hasMore && mode !== 'manual') {
          <div
            #sentinel
            class="paged-feed__sentinel"
            aria-hidden="true"
          ></div>
        }
      }
    </section>
  `,
  styles: [`
    .paged-feed {
      display: grid;
      gap: 12px;
      width: 100%;
      min-width: 0;
    }

    .paged-feed__items {
      display: grid;
      gap: 12px;
      width: 100%;
      min-width: 0;
    }

    .paged-feed__state,
    .paged-feed__append-state {
      display: grid;
      place-items: center;
      gap: 10px;
      width: 100%;
      min-height: 56px;
      padding: 12px;
      border: 1px dashed #dbe2ea;
      border-radius: 12px;
      background: #ffffff;
      color: #475569;
      text-align: center;
      box-sizing: border-box;
    }

    .paged-feed__state--error {
      border-color: #fecaca;
      background: #fef2f2;
      color: #b91c1c;
    }

    .paged-feed__action {
      height: 36px;
      border-radius: 8px;
      border: 1px solid #2563eb;
      background: #ffffff;
      color: #2563eb;
      padding: 0 12px;
      cursor: pointer;
      font-size: 13px;
    }

    .paged-feed__action--primary {
      background: #2563eb;
      color: #ffffff;
    }

    .paged-feed__action:disabled {
      opacity: 0.65;
      cursor: not-allowed;
    }

    .paged-feed__sentinel {
      width: 100%;
      height: 1px;
    }
  `],
})
export class PagedFeedComponent<TItem = unknown> implements AfterViewInit, OnChanges, OnDestroy {
  @Input({ required: true }) public items: TItem[] = [];
  @Input() public itemTemplate: TemplateRef<unknown> | null = null;
  @Input() public collectionTemplate: TemplateRef<unknown> | null = null;
  @Input() public mode: PagedFeedMode = 'manual';
  @Input() public hasMore = false;
  @Input() public loading = false;
  @Input() public loadingMore = false;
  @Input() public errorText: string | null = null;
  @Input() public itemsClass = '';
  @Input() public rootMargin = '240px';
  @Input() public loadingText = 'Loading...';
  @Input() public emptyText = 'No items';
  @Input() public retryText = 'Retry';
  @Input() public loadMoreText = 'Load more';
  @Input() public loadingMoreText = 'Loading more...';
  @Input() public trackBy: ((index: number, item: TItem) => unknown) | null = null;

  @Output() public readonly loadMore = new EventEmitter<void>();
  @Output() public readonly retry = new EventEmitter<void>();
  @Output() public readonly reachedEnd = new EventEmitter<void>();

  @ViewChild('sentinel')
  private sentinel?: ElementRef<HTMLElement>;

  private observer: IntersectionObserver | null = null;
  private autoLoadLocked = false;
  private endEmitted = false;

  public ngAfterViewInit(): void {
    this.bindObserver();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('loading' in changes || 'loadingMore' in changes) {
      if (!this.loading && !this.loadingMore) {
        this.autoLoadLocked = false;
      }
    }

    if ('hasMore' in changes) {
      if (!changes['hasMore'].firstChange && !this.hasMore && !this.endEmitted) {
        this.reachedEnd.emit();
        this.endEmitted = true;
      }
      if (this.hasMore) {
        this.endEmitted = false;
      }
    }

    if (
      'mode' in changes ||
      'rootMargin' in changes ||
      'hasMore' in changes ||
      'items' in changes
    ) {
      queueMicrotask(() => this.bindObserver());
    }
  }

  public ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  protected trackItem(index: number, item: TItem): unknown {
    return this.trackBy ? this.trackBy(index, item) : item;
  }

  private bindObserver(): void {
    this.observer?.disconnect();
    this.observer = null;

    if (this.mode === 'manual' || !this.hasMore || typeof IntersectionObserver === 'undefined') {
      return;
    }

    const target = this.sentinel?.nativeElement;
    if (!target) {
      return;
    }

    this.observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) {
        return;
      }

      if (this.autoLoadLocked || this.loading || this.loadingMore || !this.hasMore) {
        return;
      }

      this.autoLoadLocked = true;
      this.loadMore.emit();
    }, {
      root: null,
      rootMargin: this.rootMargin,
      threshold: 0,
    });

    this.observer.observe(target);
  }
}
